import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  Languages, 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  X, 
  BookOpen, 
  CheckCircle2, 
  Clock, 
  FileText, 
  ChevronRight, 
  Sparkles,
  Users,
  CheckSquare,
  Square
} from 'lucide-react';
import { TranslationProject, TranslationStage, TranslationChapterProgress } from '@ftt/shared';
import { TRANSLATION_STAGES_CONFIG, INDIAN_LANGUAGES_LIST } from '@ftt/shared';

export const AdminTranslationsPage: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('ALL');
  const [selectedStage, setSelectedStage] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState<TranslationProject | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Form State
  const [sourceTitle, setSourceTitle] = useState('');
  const [targetTitle, setTargetTitle] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('English');
  const [targetLanguage, setTargetLanguage] = useState('Hindi');
  const [originalAuthor, setOriginalAuthor] = useState('');
  const [originalPublisher, setOriginalPublisher] = useState('FOR THE TRUTH PRESS PRIVATE LIMITED');
  const [rightsPartnerName, setRightsPartnerName] = useState('Crossway Books');
  const [stage, setStage] = useState<TranslationStage>('INITIAL_DRAFT');
  const [totalChapters, setTotalChapters] = useState(10);
  const [totalWords, setTotalWords] = useState(60000);
  const [assignedTranslatorName, setAssignedTranslatorName] = useState('David K.');
  const [theologicalReviewerName, setTheologicalReviewerName] = useState('Jonathan D.');
  const [targetPubDate, setTargetPubDate] = useState('');
  const [budgetINR, setBudgetINR] = useState(180000);
  const [paidINR, setPaidINR] = useState(60000);
  const [notes, setNotes] = useState('');

  const queryClient = useQueryClient();

  const { data: translationsRes, isLoading } = useQuery({
    queryKey: ['admin-translations', { language: selectedLanguage, stage: selectedStage, search: searchQuery }],
    queryFn: () => {
      let url = '/admin/translations';
      const params = new URLSearchParams();
      if (selectedLanguage !== 'ALL') params.append('language', selectedLanguage);
      if (selectedStage !== 'ALL') params.append('stage', selectedStage);
      if (searchQuery) params.append('search', searchQuery);
      if (params.toString()) url += `?${params.toString()}`;
      return api.get(url).then((res) => res.data.data);
    },
  });

  const translations: TranslationProject[] = translationsRes || [];

  const createMutation = useMutation({
    mutationFn: (payload: any) => api.post('/admin/translations', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-translations'] });
      setIsCreateModalOpen(false);
      resetForm();
    },
  });

  const updateStageMutation = useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: TranslationStage }) => 
      api.put(`/admin/translations/${id}`, { stage }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-translations'] });
      if (selectedProject?.id === data.data.data.id) {
        setSelectedProject(data.data.data);
      }
    },
  });

  const updateChapterMutation = useMutation({
    mutationFn: ({ id, chapterNumber, updates }: { id: string; chapterNumber: number; updates: any }) =>
      api.put(`/admin/translations/${id}/chapters/${chapterNumber}`, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-translations'] });
      setSelectedProject(data.data.data);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/translations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-translations'] });
      if (selectedProject) setSelectedProject(null);
    },
  });

  const resetForm = () => {
    setSourceTitle('');
    setTargetTitle('');
    setSourceLanguage('English');
    setTargetLanguage('Hindi');
    setOriginalAuthor('');
    setOriginalPublisher('FOR THE TRUTH PRESS PRIVATE LIMITED');
    setRightsPartnerName('Crossway Books');
    setStage('INITIAL_DRAFT');
    setTotalChapters(10);
    setTotalWords(60000);
    setAssignedTranslatorName('David K.');
    setTheologicalReviewerName('Jonathan D.');
    setTargetPubDate('');
    setBudgetINR(180000);
    setPaidINR(60000);
    setNotes('');
  };

  const activeProject = selectedProject || translations[0] || null;

  return (
    <div className="space-y-6 font-sans text-slate-900 antialiased">
      {/* 1. Header with High Visibility */}
      <div className="bg-white rounded-2xl md:rounded-3xl p-6 sm:p-8 shadow-sm border border-slate-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-brand-700 font-mono uppercase tracking-widest font-bold">
              VERNACULAR PIPELINE
            </span>
            <span className="text-slate-400 font-mono text-xs">&bull;</span>
            <span className="text-xs text-slate-600 font-serif italic">
              FOR THE TRUTH PRESS PRIVATE LIMITED
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-serif font-black text-slate-900 flex items-center gap-2.5 mt-1">
            <Languages className="w-7 h-7 text-[#FF6B4A]" />
            <span>Book Translation &amp; Language Projects</span>
          </h1>
          <p className="text-xs sm:text-sm text-slate-600 font-medium mt-1">
            Track theological accuracy, chapter drafting, linguistic peer review, and publication milestones across Indian languages.
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setIsCreateModalOpen(true);
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-md transition-all shrink-0 cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#FF6B4A]" />
          <span>Launch New Translation</span>
        </button>
      </div>

      {/* 2. Language Filter & Search Bar */}
      <div className="p-4 rounded-2xl bg-white border border-slate-200/90 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search translation project..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 placeholder-slate-400 focus:outline-hidden focus:bg-white focus:border-brand-500 w-56 sm:w-64 transition-colors"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600 font-medium">Language:</span>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-900 focus:outline-hidden focus:border-brand-500"
            >
              <option value="ALL">All Indian Languages</option>
              {INDIAN_LANGUAGES_LIST.map((lang) => (
                <option key={lang} value={lang}>{lang}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="text-xs text-slate-500 font-mono">
          Showing <strong>{translations.length}</strong> active translation pipelines
        </div>
      </div>

      {/* 3. Translation Pipeline Grid & Active Tracker Drawer */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Project Cards */}
        <div className="lg:col-span-5 space-y-4">
          {isLoading ? (
            <div className="py-16 text-center text-xs font-mono text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">Loading translation projects...</div>
          ) : translations.length === 0 ? (
            <div className="py-16 text-center text-xs text-slate-500 bg-white rounded-2xl border border-slate-200 shadow-sm">No translation projects found.</div>
          ) : (
            translations.map((proj) => {
              const isSelected = activeProject?.id === proj.id;
              return (
                <div
                  key={proj.id}
                  onClick={() => setSelectedProject(proj)}
                  className={`p-5 rounded-2xl border transition-all cursor-pointer space-y-3.5 shadow-sm ${
                    isSelected
                      ? 'bg-brand-50/40 border-brand-500 ring-2 ring-brand-500/20 shadow-md'
                      : 'bg-white border-slate-200/90 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[10px] font-mono uppercase font-bold text-brand-700 px-2.5 py-0.5 rounded-lg bg-brand-50 border border-brand-200">
                      {proj.projectCode} &bull; {proj.targetLanguage}
                    </span>

                    <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-lg font-bold bg-slate-100 text-slate-700 border border-slate-200">
                      {proj.progressPercentage}%
                    </span>
                  </div>

                  <div>
                    <h3 className="font-serif text-base font-bold text-slate-900 leading-snug">
                      {proj.targetTitle}
                    </h3>
                    <p className="text-xs text-slate-600 pt-0.5">
                      Source: <strong>{proj.sourceTitle}</strong> by {proj.originalAuthor}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1">
                    <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden border border-slate-200/60">
                      <div
                        className="bg-[#FF6B4A] h-2 rounded-full transition-all duration-500"
                        style={{ width: `${proj.progressPercentage}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
                      <span>{proj.completedChapters} of {proj.totalChapters} chapters approved</span>
                      <span>{proj.translatedWords.toLocaleString()} / {proj.totalWords.toLocaleString()} words</span>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-600">
                    <span>Translator: <strong className="text-slate-900">{proj.assignedTranslatorName || 'TBD'}</strong></span>
                    <span className="text-brand-700 font-mono text-[10px] font-bold">
                      {proj.stage.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Right Column: Detailed Chapter Tracker & Stage Manager */}
        {activeProject ? (
          <div className="lg:col-span-7 p-6 sm:p-8 rounded-2xl md:rounded-3xl bg-white border border-slate-200/90 space-y-6 shadow-sm">
            {/* Detail Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-slate-100 pb-5">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-lg bg-brand-50 text-brand-700 border border-brand-200 font-bold uppercase">
                    {activeProject.projectCode}
                  </span>
                  <span className="text-[10px] font-mono px-2.5 py-0.5 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 font-bold uppercase">
                    {activeProject.targetLanguage} Edition
                  </span>
                </div>
                <h2 className="text-2xl font-serif font-bold text-slate-900 pt-1">
                  {activeProject.targetTitle}
                </h2>
                <p className="text-xs text-slate-600">
                  Original: <strong className="text-slate-900">{activeProject.sourceTitle}</strong> by {activeProject.originalAuthor} ({activeProject.originalPublisher})
                </p>
              </div>

              <button
                onClick={() => {
                  if (confirm(`Delete translation project "${activeProject.targetTitle}"?`)) {
                    deleteMutation.mutate(activeProject.id);
                  }
                }}
                className="p-2 px-3 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 transition-colors text-xs font-bold inline-flex items-center gap-1.5 shrink-0 self-start border border-rose-200 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Delete</span>
              </button>
            </div>

            {/* Stage Selector Stepper */}
            <div className="space-y-2">
              <label className="text-xs font-mono uppercase tracking-wider text-slate-700 font-bold block">
                Current Workflow Stage (10-Stage Pathway)
              </label>
              <select
                value={activeProject.stage}
                onChange={(e) => updateStageMutation.mutate({ id: activeProject.id, stage: e.target.value as TranslationStage })}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-xs text-brand-700 font-bold focus:outline-hidden focus:border-brand-500"
              >
                {TRANSLATION_STAGES_CONFIG.map((stg) => (
                  <option key={stg.stage} value={stg.stage}>
                    Stage {stg.order}: {stg.label} — {stg.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Key Personnel & Budget Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 rounded-2xl bg-slate-50 border border-slate-200 text-xs">
              <div>
                <span className="text-slate-500 text-[10px] uppercase font-mono block">Assigned Translator</span>
                <span className="font-bold text-slate-900 font-serif">{activeProject.assignedTranslatorName || 'Unassigned'}</span>
              </div>
              <div>
                <span className="text-slate-500 text-[10px] uppercase font-mono block">Theological Reviewer</span>
                <span className="font-bold text-slate-900 font-serif">{activeProject.theologicalReviewerName || 'Unassigned'}</span>
              </div>
              <div>
                <span className="text-slate-500 text-[10px] uppercase font-mono block">Budget Allocation</span>
                <span className="font-bold text-emerald-700 font-mono">₹{activeProject.budgetINR.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* Chapter by Chapter Checklist */}
            <div className="space-y-3">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h4 className="font-serif text-sm font-bold text-slate-900">
                  Chapter Progress Checklist ({activeProject.completedChapters} / {activeProject.chapters.length})
                </h4>
                <span className="text-[10px] font-mono text-slate-500">Click checkboxes to update live</span>
              </div>

              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {activeProject.chapters.map((ch) => (
                  <div
                    key={ch.chapterNumber}
                    className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
                  >
                    <div className="space-y-0.5">
                      <span className="font-bold text-slate-900 block">
                        {ch.chapterTitle}
                      </span>
                      <span className="text-[10px] text-slate-500 font-mono">
                        {ch.wordCount.toLocaleString()} words {ch.translatorNotes ? `&bull; ${ch.translatorNotes}` : ''}
                      </span>
                    </div>

                    <div className="flex items-center gap-3 text-[11px] font-mono shrink-0">
                      <label className="flex items-center gap-1.5 cursor-pointer text-slate-700">
                        <input
                          type="checkbox"
                          checked={ch.draftCompleted}
                          onChange={(e) => updateChapterMutation.mutate({
                            id: activeProject.id,
                            chapterNumber: ch.chapterNumber,
                            updates: { draftCompleted: e.target.checked }
                          })}
                          className="rounded text-brand-600 bg-white border-slate-300"
                        />
                        <span>Draft</span>
                      </label>

                      <label className="flex items-center gap-1.5 cursor-pointer text-slate-700">
                        <input
                          type="checkbox"
                          checked={ch.theologicalReviewCompleted}
                          onChange={(e) => updateChapterMutation.mutate({
                            id: activeProject.id,
                            chapterNumber: ch.chapterNumber,
                            updates: { theologicalReviewCompleted: e.target.checked }
                          })}
                          className="rounded text-purple-600 bg-white border-slate-300"
                        />
                        <span>Review</span>
                      </label>

                      <label className="flex items-center gap-1.5 cursor-pointer text-emerald-700 font-bold">
                        <input
                          type="checkbox"
                          checked={ch.editorApproved}
                          onChange={(e) => updateChapterMutation.mutate({
                            id: activeProject.id,
                            chapterNumber: ch.chapterNumber,
                            updates: { editorApproved: e.target.checked }
                          })}
                          className="rounded text-emerald-600 bg-white border-slate-300"
                        />
                        <span>Approved</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        ) : (
          <div className="lg:col-span-7 p-16 text-center rounded-2xl bg-white border border-slate-200 text-slate-500 font-mono text-xs shadow-sm">
            Select a translation project to view chapter details.
          </div>
        )}

      </div>

      {/* Launch New Translation Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-3xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-brand-700 font-bold">VERNACULAR INITIATIVE</span>
                <h2 className="text-xl font-serif font-bold text-slate-900">
                  Launch New Vernacular Translation Project
                </h2>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate({
                  sourceTitle,
                  targetTitle,
                  sourceLanguage,
                  targetLanguage,
                  originalAuthor,
                  originalPublisher,
                  rightsPartnerName,
                  stage,
                  totalChapters,
                  totalWords,
                  assignedTranslatorName,
                  theologicalReviewerName,
                  targetPubDate: targetPubDate || null,
                  budgetINR,
                  paidINR,
                  notes,
                });
              }}
              className="space-y-4 text-xs font-sans"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Source English Book Title *</label>
                  <input
                    type="text"
                    required
                    value={sourceTitle}
                    onChange={(e) => setSourceTitle(e.target.value)}
                    placeholder="e.g. The Message of Acts"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Target Vernacular Title *</label>
                  <input
                    type="text"
                    required
                    value={targetTitle}
                    onChange={(e) => setTargetTitle(e.target.value)}
                    placeholder="e.g. प्रेरितों के काम का संदेश"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Target Language *</label>
                  <select
                    value={targetLanguage}
                    onChange={(e) => setTargetLanguage(e.target.value)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  >
                    {INDIAN_LANGUAGES_LIST.map((lang) => (
                      <option key={lang} value={lang}>{lang}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Original Author *</label>
                  <input
                    type="text"
                    required
                    value={originalAuthor}
                    onChange={(e) => setOriginalAuthor(e.target.value)}
                    placeholder="e.g. John Stott"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Rights Partner</label>
                  <input
                    type="text"
                    value={rightsPartnerName}
                    onChange={(e) => setRightsPartnerName(e.target.value)}
                    placeholder="e.g. InterVarsity Press"
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Total Chapters</label>
                  <input
                    type="number"
                    value={totalChapters}
                    onChange={(e) => setTotalChapters(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Total Words</label>
                  <input
                    type="number"
                    value={totalWords}
                    onChange={(e) => setTotalWords(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Budget (INR ₹)</label>
                  <input
                    type="number"
                    value={budgetINR}
                    onChange={(e) => setBudgetINR(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Assigned Translator</label>
                  <input
                    type="text"
                    value={assignedTranslatorName}
                    onChange={(e) => setAssignedTranslatorName(e.target.value)}
                    placeholder="e.g. David K."
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-slate-700 font-semibold block">Theological Reviewer</label>
                  <input
                    type="text"
                    value={theologicalReviewerName}
                    onChange={(e) => setTheologicalReviewerName(e.target.value)}
                    placeholder="e.g. Jonathan D."
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-900 focus:outline-hidden focus:border-brand-500"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-100 font-bold transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-6 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold shadow-md transition-colors cursor-pointer disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Launching...' : 'Launch Translation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
